<template>
  <chart-container
    :titleChart="titleChart"
    @onCSVDownload="downloadCsv(titleChart)"
    @zoomEnabled="zoomGraph(true)"
    @zoomDisabled="zoomGraph(false)"
    :downloadId="downloadId"
    :specificSiteId="specificSiteId"
    :zoomGraphValue="zoomGraphValue"
    :downloadShow="dataSource.categories && Object.keys(dataSource.categories).length > 0"
    class="bg-gradient-blue"
    titleTooltip="<div style='font-size:10px;'>This chart graphs the overall average compliance rate for the selected time period. The graph shows the change in compliance for activities that are collected within the THREAD Platform for this study including: clinical data activities, comprehension quiz completion, eDRO, participant tasks for telehealth, and site team tasks.<br><br> The dotted lines on the chart represent standard deviation bands for the overall compliance rate of the study. With this guidance, you can identify dates or periods of time where a high variance in compliance exists compared to the average rate. Hover over the standard deviation line or the points on the chart for more information. To ensure accuracy, the standard deviation bands only appear when there is at least a month’s worth of study data in the filtered date range. <br><br> You can modify the dashboard filters to see compliance rates attributed to specific countries or sites.</div>"
    :tooltipHtmlContent="true"
    :showThreadLogoInUnexpand="true"
  >
    <fusioncharts style="width: 100%" :type="type" :width="width" :height="height" :dataFormat="dataFormat" :dataSource="dataSource" dataEmptyMessage="No Data In" ref="complianceRateOverTime" v-if="dataSource.categories && Object.keys(dataSource.categories).length > 0" slot="body" />
    <div slot="body" class="text-left min-height-0" v-if="dataSource.categories && Object.keys(dataSource.categories).length > 0">
      <span @click="zoomOutChart()" class="pl-10 pr-5 remove-zoom-icons">
        <img src="/static/img/icon-zoomout.svg" class="download_graph cursor_pointer" aria-haspopup="true" aria-expanded="false" title="Zoom Out" />
      </span>
      <span @click="zoomInChart()" class="remove-zoom-icons">
        <img src="/static/img/icon-zoomin.svg" class="download_graph cursor_pointer" aria-haspopup="true" aria-expanded="false" title="Zoom In" />
      </span>
    </div>
    <div v-else class="align-center p-60" slot="body">
      <div class="card-text-title no-data" v-if="dataLoaded">No Data In</div>
    </div>
  </chart-container>
</template>

<script>
import _cloneDeep from "lodash/cloneDeep";
import * as dManagement from "@/services/visualization/data-management";
import * as studyComplianceManagement from "@/services/visualization/study-compliance";
import { getColorPalette } from "@/services/visualization/utils";
import ChartContainer from "./chart-container-dark-blue.vue";
export default {
  components: {
    ChartContainer
  },

  props: {
    titleChart: { type: String, default: "" },
    downloadId: { type: String, default: "" },
    zoomGraphValue: { type: String, default: "" },
    specificSiteId: [Object, Array],
    countrySelected: [Array, String],
    sitesSelected: [Array, String],
    rawData: { type: [Object, Array], default: () => {} },
    dataLoaded: { type: Boolean, default: false }
  },

  watch: {
    rawData: function(newVal) {
      const response = studyComplianceManagement.getComplianceRateOverTimeData(newVal, this.countrySelected, this.sitesSelected);
      this.setGraphDataAndConfigs(response);
    },
    countrySelected: function(newVal) {
      const response = studyComplianceManagement.getComplianceRateOverTimeData(this.rawData, newVal, this.sitesSelected);
      this.setGraphDataAndConfigs(response);
    },
    sitesSelected: function(newVal) {
      const response = studyComplianceManagement.getComplianceRateOverTimeData(this.rawData, this.countrySelected, newVal);
      this.setGraphDataAndConfigs(response);
    },
    zoomTrigger: function() {
      this.setGraphDataAndConfigs();
    },
    zoomGraphValue: function(newVal) {
      this.zoomGraphValueChild = newVal;
      if (newVal == "") {
        this.height = "250";
      }
      if (newVal) {
        this.zoomTrigger = true;
      } else {
        this.zoomTrigger = false;
      }
    }
  },

  data() {
    return {
      zoomTrigger: false,
      zoomGraphValueChild: "",
      type: "scrollcombidy2d",
      width: "100%",
      height: "250",
      dataFormat: "json",
      dataSource: {
        chart: {
          showDivLineSecondaryValue: "0",
          showSecondaryLimits: "0",
          outCnvBaseFontColor: "#D9D9D9",
          outCnvBaseFontSize: "10",
          canvasLeftPadding: "35",
          canvasRightPadding: "35",
          plotHighlightEffect: "fadeout",
          divlines: "5",
          divlinealpha: "0",
          xAxisname: "Week Ending Date",
          xAxisNameFontColor: "#D9D9D9",
          xAxisNameFontSize: "9",
          xAxisValueFontColor: "#D9D9D9",
          xAxisValueFontSize: "10",
          pYAxisName: "Compliance Rate %",
          pYAxisNameFontColor: "#D9D9D9",
          pYAxisNameFontSize: "9",
          pYAxisValueFontColor: "#D9D9D9",
          pYAxisValueFontSize: "10",
          legendItemFont: "Roboto",
          legendPosition: "right",
          legendItemFontColor: "#D9D9D9",
          legendItemFontSize: "10",
          legendItemHiddenColor: "#7B8BA2",
          NumberSuffix: "%",
          showValues: "1",
          numVisiblePlot: "10",
          flatScrollBars: "1",
          scrollheight: "10",
          minimizeWrappinginLegend: false,
          legendCaptionAlignment: "center",
          plottooltext: "$seriesname: $value",
          drawanchors: "0",
          anchorAlpha: "100",
          hideyaxis: true,
          pyaxisminvalue: "0",
          pyaxismaxvalue: "100",
          theme: "fusion",
          showXAxisLine: true,
          showYAxisLine: true,
          xaxislinethickness: "1",
          yaxislinethickness: "1",
          xAxisLineColor: "#b0bac9",
          yAxisLineColor: "#b0bac9",
          showsyaxisline: false,
          chartBottomMargin: "0",
          displaytooltext: "1",
          paletteColors: getColorPalette("gradient"),
          bgAlpha: "0",
          scrollToEnd: "1",
          scrollPadding: 1
        },
        categories: [],
        dataset: [
          {
            /* This data is sample data, real data is variable*/
            seriesName: "Overall Average Compliance Rate",
            parentYAxis: "P",
            renderAs: "line",
            showValues: "0",
            pyvaluesuffix: "%",
            plottooltext: "$seriesname: $value%",
            color: "#D6B2CE"
          }
        ],
        trendlines: []
      },
      curZoomLevel: 0,
      maxZoomLevel: 2
    };
  },

  methods: {
    zoomGraph(status) {
      if (status) {
        this.zoomGraphValueChild = true;
        this.height = "400";
        this.$emit("zoomEnabled");
      } else {
        this.zoomGraphValueChild = false;
        this.height = "250";
        this.$emit("zoomDisabled");
      }
    },
    downloadCsv(titleChart) {
      setTimeout(() => {
        const header = [];
        const newArr = [];
        const response = studyComplianceManagement.getComplianceRateOverTimeData(this.rawData, this.countrySelected, this.sitesSelected);
        newArr.push([titleChart, "", "Week Ending Dates"]);
        newArr.push(["Mean Compliance Rate %", response.meanComplianceRate]);
        newArr.push(["Compliance Rate Standard Deviation", response.complianceRateStandardDeviation]);
        newArr.push(["", ""]);
        newArr.push(["", ""]);
        newArr.push(["Overall Compliance Rate %", ""]);
        response.csvData.forEach(data => {
          newArr[4].push(" " + data.date);
          newArr[5].push(data.compliance_rate);
        });
        dManagement.downloadCSV(titleChart, header, newArr, this.specificSiteId);
      }, 1);
    },
    getNumVisPlotByZoom() {
      let numVisPlot = 0;
      switch (this.curZoomLevel) {
        case 0:
          numVisPlot = 10;
          break;
        case 1:
          numVisPlot = 8;
          break;
        case 2:
          numVisPlot = 6;
          break;
        default:
          break;
      }
      return numVisPlot;
    },
    zoomInChart() {
      if (this.curZoomLevel < this.maxZoomLevel) {
        this.curZoomLevel = this.curZoomLevel + 1;
        const chartRef = this.$refs.complianceRateOverTime.chartObj;
        const data = chartRef.getJSONData();
        const numVisPlot = this.getNumVisPlotByZoom();
        data.chart.numvisibleplot = numVisPlot;
        chartRef.setJSONData(data);
      }
    },
    zoomOutChart() {
      if (this.curZoomLevel > 0) {
        this.curZoomLevel = this.curZoomLevel - 1;
        const chartRef = this.$refs.complianceRateOverTime.chartObj;
        const data = chartRef.getJSONData();
        const numVisPlot = this.getNumVisPlotByZoom();
        data.chart.numvisibleplot = numVisPlot;
        chartRef.setJSONData(data);
      }
    },
    setGraphDataAndConfigs(response = null) {
      const dataSources = _cloneDeep(this.dataSource);
      let drawAnchors;
      if (response) {
        drawAnchors = response.categories.length > 1 ? "0" : "1";
        if (response.categories.length > 0) {
          dataSources.categories[0] = { category: response.categories };
        } else {
          dataSources.categories = [];
        }
        dataSources.dataset[0].data = response.data[0];
        dataSources.trendlines[0] = { line: response.lines };
        dataSources.chart.drawanchors = drawAnchors;
      }
      this.dataSource = dataSources;
      if (this.$refs.complianceRateOverTime) {
        const chartRef = this.$refs.complianceRateOverTime.chartObj;
        const data = chartRef.getJSONData();
        data.chart.drawanchors = drawAnchors;
        if (this.zoomTrigger) {
          this.height = "400";
        } else {
          this.height = "250";
        }
        chartRef.setJSONData(data);
      }
    }
  }
};
</script>

<style lang="scss">
.bg-gradient-blue {
  .fusioncharts-container > svg {
    background-color: unset !important;
  }
}
</style>
